// Turns dictated measurements into installer notation: "quarenta e quatro três quartos" → 44 3/4,
// "twenty one and a half by thirty inches" → 21 1/2 x 30". Words that are not clearly a
// measurement (a lone "um", "one", "dos") are left untouched.

const UNITS = {
  zero: 0, um: 1, uma: 1, dois: 2, duas: 2, "três": 3, tres: 3, quatro: 4, cinco: 5, seis: 6, sete: 7, oito: 8, nove: 9,
  dez: 10, onze: 11, doze: 12, treze: 13, catorze: 14, quatorze: 14, quinze: 15, dezesseis: 16, dezasseis: 16, dezessete: 17,
  dezassete: 17, dezoito: 18, dezenove: 19, dezanove: 19,
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
  thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19,
  uno: 1, un: 1, una: 1, dos: 2, cuatro: 4, siete: 7, ocho: 8, nueve: 9, diez: 10, once: 11, doce: 12, trece: 13,
  catorce: 14, quince: 15, "dieciséis": 16, dieciseis: 16, diecisiete: 17, dieciocho: 18, diecinueve: 19,
  veintiuno: 21, "veintiún": 21, veintiun: 21, "veintidós": 22, veintidos: 22, "veintitrés": 23, veintitres: 23, veinticuatro: 24,
  veinticinco: 25, "veintiséis": 26, veintiseis: 26, veintisiete: 27, veintiocho: 28, veintinueve: 29,
};
const TENS = {
  vinte: 20, trinta: 30, quarenta: 40, cinquenta: 50, cinqüenta: 50, sessenta: 60, setenta: 70, oitenta: 80, noventa: 90,
  twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90,
  veinte: 20, treinta: 30, cuarenta: 40, cincuenta: 50, sesenta: 60, ochenta: 80,
};
const HUNDREDS = {
  cem: 100, cento: 100, duzentos: 200, duzentas: 200, trezentos: 300, quatrocentos: 400, quinhentos: 500, seiscentos: 600,
  setecentos: 700, oitocentos: 800, novecentos: 900,
  hundred: 100,
  cien: 100, ciento: 100, doscientos: 200, trescientos: 300, cuatrocientos: 400, quinientos: 500, seiscientos: 600,
  setecientos: 700, ochocientos: 800, novecientos: 900,
};
const CONNECTORS = new Set(["e", "and", "y", "com", "with", "con"]);
const ARTICLES = new Set(["a", "um", "uma", "un", "una", "one"]);
// denominator words → value; "half"-like words imply numerator 1
const DENOMS = {
  meio: 2, meia: 2, metade: 2, half: 2, medio: 2, media: 2, mitad: 2,
  quarto: 4, quartos: 4, quarter: 4, quarters: 4, cuarto: 4, cuartos: 4, fourth: 4, fourths: 4,
  oitavo: 8, oitavos: 8, eighth: 8, eighths: 8, octavo: 8, octavos: 8,
  "dezesseis-avos": 16, sixteenth: 16, sixteenths: 16, dieciseisavo: 16, dieciseisavos: 16,
};
const AVOS = new Set(["avos", "avo"]);
const INCH = new Set(["polegada", "polegadas", "inch", "inches", "pulgada", "pulgadas"]);
const FEET = new Set(["pé", "pés", "pe", "pes", "foot", "feet", "pie", "pies"]);
const BY = new Set(["por", "by", "x", "vezes", "times"]);

const clean = (w) => w.toLowerCase().replace(/[.,;:!?]+$/g, "");
const isDigits = (w) => /^\d+$/.test(w);

/** Parse an integer written in words (or digits) starting at i. Returns {value, next, words} or null. */
function parseInt_(tokens, i) {
  if (i >= tokens.length) return null;
  if (isDigits(tokens[i])) return { value: parseInt(tokens[i], 10), next: i + 1, words: 1 };
  let value = 0, j = i, words = 0, stage = "start"; // start → hundreds → tens → units
  while (j < tokens.length) {
    const w = tokens[j];
    if (words > 0 && CONNECTORS.has(w) && j + 1 < tokens.length) {
      const nx = tokens[j + 1];
      const ok = (stage === "hundreds" && (TENS[nx] != null || UNITS[nx] != null)) || (stage === "tens" && UNITS[nx] != null && UNITS[nx] < 10);
      if (!ok) break;
      j += 1; continue;
    }
    if (HUNDREDS[w] != null && stage === "start") {
      value += HUNDREDS[w]; stage = "hundreds";
    } else if (w === "hundred" && stage === "units" && value < 10) {
      value *= 100; stage = "hundreds";
    } else if (TENS[w] != null && (stage === "start" || stage === "hundreds")) {
      value += TENS[w]; stage = "tens";
    } else if (UNITS[w] != null && (stage === "start" || stage === "hundreds" || (stage === "tens" && UNITS[w] < 10))) {
      // "one" is also an article ("one quarter") — leave it for the fraction parser
      if (w === "one" && (DENOMS[tokens[j + 1]] != null || tokens[j + 1] === "sixteenth")) break;
      value += UNITS[w]; stage = UNITS[w] >= 20 ? "tens" : "units";
      if (UNITS[w] >= 10) { j += 1; words += 1; break; }
    } else break;
    j += 1; words += 1;
  }
  return words ? { value, next: j, words } : null;
}

/** Parse a fraction ("três quartos", "and a half", "one sixteenth", "um dezesseis avos") at i. */
function parseFraction(tokens, i) {
  let j = i;
  if (j < tokens.length && CONNECTORS.has(tokens[j])) j += 1;
  if (j < tokens.length && ARTICLES.has(tokens[j]) && (DENOMS[tokens[j + 1]] != null || UNITS[tokens[j + 1]] === 16)) {
    const d = tokens[j + 1];
    if (DENOMS[d] != null) return { num: 1, den: DENOMS[d], next: j + 2 };
    if (AVOS.has(tokens[j + 2])) return { num: 1, den: 16, next: j + 3 };
  }
  if (j < tokens.length && DENOMS[tokens[j]] != null && DENOMS[tokens[j]] === 2) return { num: 1, den: 2, next: j + 1 };
  const n = parseInt_(tokens, j);
  if (n && n.value >= 1 && n.value <= 15) {
    const d = tokens[n.next];
    if (DENOMS[d] != null && DENOMS[d] !== 2) return { num: n.value, den: DENOMS[d], next: n.next + 1 };
    if (UNITS[d] === 16 && AVOS.has(tokens[n.next + 1])) return { num: n.value, den: 16, next: n.next + 2 };
  }
  return null;
}

/** Parse one measurement (integer and/or fraction, optional unit) at i. */
function parseMeasure(tokens, i) {
  const n = parseInt_(tokens, i);
  let next = n ? n.next : i;
  const f = parseFraction(tokens, next);
  if (!n && !f) return null;
  if (f) next = f.next;
  let unit = "";
  if (INCH.has(tokens[next])) { unit = '"'; next += 1; } else if (FEET.has(tokens[next])) { unit = "'"; next += 1; }
  const parts = [];
  if (n) parts.push(String(n.value));
  if (f) parts.push(`${f.num}/${f.den}`);
  const spelled = !!n && !isDigits(tokens[i]);
  // a bare fraction ("um quarto", "no meio") is everyday speech — only convert with a number or a unit
  return { text: parts.join(" ") + unit, next, strong: (!!n && !!f) || !!unit || (spelled && n.words >= 2), spelled };
}

export function normalizeSpokenMeasure(input) {
  if (!input || !/[a-záéíóúçãõ]/i.test(input)) return input;
  const raw = input.split(/\s+/).filter(Boolean);
  const tokens = raw.flatMap((w) => clean(w).split("-")).filter(Boolean);
  const rawTokens = raw.flatMap((w) => w.split("-")).filter(Boolean);
  const out = [];
  let i = 0;
  while (i < tokens.length) {
    const a = parseMeasure(tokens, i);
    if (a) {
      let text = a.text, next = a.next, strong = a.strong;
      if (BY.has(tokens[next])) {
        const b = parseMeasure(tokens, next + 1);
        if (b && (b.spelled || a.spelled || b.strong || a.strong)) { text = `${text} x ${b.text}`; next = b.next; strong = true; }
      }
      if (strong) { out.push(text); i = next; continue; }
    }
    out.push(rawTokens[i]);
    i += 1;
  }
  return out.join(" ");
}
